<template>
  <div>
    <tree-node
      v-for="child in spaces"
      :key="child.id"
      :id="child.id"
      :name="child.name"
      :type="child.type"
      :tenant="child.type === 'tenant' ? child.id : child.tenantId"
      :children="child.children"
      :depth="0"
      :handle-checked="handleChecked"
      :activeIds="props.value"
      :is-searching="isSearching"
      :disable-ungrouped="props.disableUngrouped"
      :disable-groups="props.disableGroups"
      :disable-tenants="props.disableTenants"
      :identifier="child.identifier"
      :show-identifier="props.showIdentifier"
    ></tree-node>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, ref, watch } from "vue";
import { cloneDeep } from "lodash";
import TreeNode from "@/Tailwind/Components/Tree/TreeNode";
import { useLocations } from "@/Tailwind/Mixins/useLocations";

const emit = defineEmits(["update:modelValue", "checked"]);

const props = defineProps({
  spaces: Array,
  fullHierarchy: Array,
  value: [Array], // Array of space ids
  single: Boolean,
  tenantId: [Array, String],
  isSearching: Boolean,
  cascadeCheck: Boolean,
  disableUngrouped: Boolean,
  disableGroups: Boolean,
  disableTenants: Boolean,
  showIdentifier: Boolean,
});

const { findLocationRecursive } = useLocations();

const isSearching = ref(props.isSearching);
watch(
  () => props.isSearching,
  () => {
    isSearching.value = props.isSearching;
  }
);

const spaces = ref(clonePropsSpaces());
watch(
  () => props.spaces,
  () => (spaces.value = clonePropsSpaces())
);

function clonePropsSpaces() {
  return cloneDeep(props.spaces).filter((root) => {
    if (!props.tenantId) {
      return true;
    }

    if (Array.isArray(props.tenantId)) {
      return props.tenantId.includes(root.id);
    }

    return props.tenantId === root.id;
  });
}

function handleChecked(space, children, state) {
  if (props.single) {
    emit("checked", state ? space : null);
    return;
  }

  if (space.type === "tenant") {
    const tenant = (props.fullHierarchy ?? props.spaces).find(
      (tenant) => tenant.id === space.id
    );

    children = tenant.children;
  } else if (props.fullHierarchy) {
    // Find all children based on the full hierachy
    children = findLocationRecursive(props.fullHierarchy, space.id)?.children;
  }

  const affectedSpaceIds = [
    ...flatten(props.cascadeCheck ? children : []),
    space,
  ].map((space) => space.id);

  if (state === true) {
    const spaces = [...new Set([...props.value, ...affectedSpaceIds])];

    emit("checked", spaces);
    return;
  }

  emit(
    "checked",
    props.value.filter((space) => !affectedSpaceIds.includes(space))
  );
}

function flatten(hierarchy, nodes = []) {
  for (const node of hierarchy) {
    nodes.push(node);
    for (const child of node.children) {
      nodes.push(child);
      flatten(child.children, nodes);
    }
  }

  return nodes;
}
</script>
